import { ReactNode, useCallback, useContext, useEffect, useState } from 'preact/compat';

import { Camera, Habitat } from '@/queries/habitats/types';
import { useSelector } from '@/redux/helper';
import { GlobalsContext } from '@/shared/context';

import { HabitatLiveStreamPlayerContext } from './contexts';

interface HabitatLiveStreamPlayerRootProps {
  children: ReactNode;
  habitat: Habitat;
}

const HabitatLiveStreamPlayerRoot = ({ habitat, children }: HabitatLiveStreamPlayerRootProps) => {
  const userId = useSelector((state) => state.user.userId);
  const { socket } = useContext(GlobalsContext);

  const [cameraId, setCameraId] = useState<string | undefined>(undefined);

  const selectedCamera = habitat.cameras?.find((camera) => camera._id === cameraId);
  const defaultCamera = habitat.cameras?.[0];
  const isCameraAvailable = !!selectedCamera;

  // TEMP: we always set a camera to avoid flickering when the habitat is loading in ambient mode
  const camera = selectedCamera ?? defaultCamera;
  const streamId = camera?.streamKey;

  const setCamera = useCallback((camera: Camera) => {
    setCameraId(camera._id);
  }, []);

  useEffect(() => {
    if (socket && userId && habitat?._id) {
      socket.emit('joinRoom', {
        room: habitat?._id,
        userId,
        streamKey: streamId ?? 'offline',
      });
      return () => {
        socket.emit('leaveRoom', { room: habitat?._id });
      };
    }
    return undefined;
  }, [habitat?._id, socket, streamId, userId]);

  useEffect(() => {
    if (socket && userId && camera?._id) {
      socket.emit('joinRoom', {
        room: camera._id,
        userId,
      });
      return () => {
        socket.emit('leaveRoom', { room: camera._id });
      };
    }
    return undefined;
  }, [socket, userId, camera?._id]);

  // reset to first camera when habitat cameras array length changes
  useEffect(() => {
    if (!isCameraAvailable && camera?._id) {
      setCameraId(camera._id);
    }
  }, [isCameraAvailable, camera?._id]);

  return (
    <HabitatLiveStreamPlayerContext.Provider value={{ habitat, camera, setCamera }}>
      {children}
    </HabitatLiveStreamPlayerContext.Provider>
  );
};

export default HabitatLiveStreamPlayerRoot;
